import { useState, useSyncExternalStore } from 'react';
import { changePassword } from '../../api/users';
import { Lock } from '../../icons';
import { tSettings, getSettingsLang, subscribeSettingsLang } from '../../lib/settings-i18n';
import { logout } from '../../lib/logout';
import { markSessionEndReason } from '../../lib/session-end-reason';

/**
 * settings-module-v1 SET-006 — 비밀번호 변경.
 *
 * 정책:
 *   - 클라이언트 validation: 8+ chars + 영문/숫자/특수문자 3종 이상 + new === confirm
 *   - 서버 validation 동일 (drift 차단) — 422 응답 시 violations 배열 표시
 *   - 401 (current 불일치 / SSO) / 422 (정책·재사용) / 200 (성공)
 */

const MIN_LENGTH = 8;

function validateClient(newPw: string, confirm: string): string | null {
  if (newPw !== confirm) return tSettings('settings.password.err_mismatch');
  if (newPw.length < MIN_LENGTH) return tSettings('settings.password.err_min_length', { n: MIN_LENGTH });
  let categories = 0;
  if (/[a-zA-Z]/.test(newPw)) categories++;
  if (/[0-9]/.test(newPw)) categories++;
  if (/[^a-zA-Z0-9]/.test(newPw)) categories++;
  if (categories < 3) return tSettings('settings.password.err_categories');
  return null;
}

export function PasswordSection() {
  useSyncExternalStore(subscribeSettingsLang, getSettingsLang, getSettingsLang);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    const clientErr = validateClient(newPassword, confirmPassword);
    if (clientErr) { setError(clientErr); return; }
    if (!currentPassword) { setError(tSettings('settings.password.err_current_required')); return; }

    setSubmitting(true);
    try {
      await changePassword({ currentPassword, newPassword });
      /**
       * ★ 비밀번호를 바꾸면 서버가 이 사용자의 토큰을 모두 폐기한다(PR #1365 부터 즉시 · 종전에도 refresh 는 폐기).
       *   다음 요청이 401 로 로그인 화면에 떨어질 수 있으므로 **먼저** 이유를 남긴다 — 그러면 떨어져도
       *   로그인 화면이 「비밀번호가 바뀌었다」를 말한다. 이 화면에서도 아래에 같은 안내와 단추를 둔다.
       */
      markSessionEndReason('password-changed', { overwrite: true });
      setSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      const status = err?.response?.status;
      const code = err?.response?.data?.code;
      const message = err?.response?.data?.message ?? err.message;
      const violations = err?.response?.data?.violations as string[] | undefined;
      if (status === 401 && code === 'INVALID_PASSWORD') {
        setError(tSettings('settings.password.err_invalid'));
      } else if (status === 422 && code === 'PASSWORD_POLICY_VIOLATED') {
        setError(tSettings('settings.password.err_policy', { violations: violations?.join(', ') ?? message }));
      } else if (status === 422 && code === 'PASSWORD_REUSED') {
        setError(tSettings('settings.password.err_reused'));
      } else {
        setError(tSettings('settings.password.err_change', { message }));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="settings-section" aria-labelledby="pw-h2">
      <h2 id="pw-h2">{tSettings('settings.password.heading')}</h2>

      <form onSubmit={onSubmit}>
        <div className="profile-grid">
          <div className="profile-field profile-field--full">
            <label htmlFor="pw-current">{tSettings('settings.password.label_current')}</label>
            <input id="pw-current" type="password" value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)} autoComplete="current-password" />
          </div>
          <div className="profile-field">
            <label htmlFor="pw-new">{tSettings('settings.password.label_new')}</label>
            <input id="pw-new" type="password" value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)} autoComplete="new-password" />
          </div>
          <div className="profile-field">
            <label htmlFor="pw-confirm">{tSettings('settings.password.label_confirm')}</label>
            <input id="pw-confirm" type="password" value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)} autoComplete="new-password" />
          </div>
        </div>

        <p className="profile-form-hint"><Lock size={13} aria-hidden /> {tSettings('settings.password.hint')}</p>

        <div className="settings-actions">
          <button type="submit" className="settings-btn" disabled={submitting}>
            {submitting ? tSettings('settings.password.btn_changing') : tSettings('settings.password.btn_change')}
          </button>
        </div>

        {error && <p className="settings-error" role="alert">{error}</p>}
        {success && <p className="settings-success">{tSettings('settings.password.msg_success')}</p>}
        {success && (
          <div className="settings-relogin" role="status">
            <span>{tSettings('settings.password.msg_relogin')}</span>
            <button type="button" className="settings-btn" onClick={() => void logout()}>
              {tSettings('settings.password.btn_relogin')}
            </button>
          </div>
        )}
      </form>
    </section>
  );
}
